import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { toast } from 'sonner';
import { Plus, Loader2, Edit2, Trash2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';

const PrioritiesTab = () => {
  const [priorities, setPriorities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [formData, setFormData] = useState({ id: null, name: '', color: '#3b82f6', level: 1 });

  useEffect(() => {
    fetchPriorities();
  }, []);

  const fetchPriorities = async () => {
    try {
      const response = await api.get('/priorities');
      setPriorities(response.data);
    } catch (error) {
      toast.error('Failed to fetch priorities');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...formData, level: parseInt(formData.level) };
      if (editMode) {
        await api.put(`/priorities/${formData.id}`, payload);
        toast.success('Priority updated successfully!');
      } else {
        await api.post('/priorities', payload);
        toast.success('Priority created successfully!');
      }
      setDialogOpen(false);
      setFormData({ id: null, name: '', color: '#3b82f6', level: 1 });
      setEditMode(false);
      fetchPriorities();
    } catch (error) {
      toast.error(editMode ? 'Failed to update priority' : 'Failed to create priority');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (priority) => {
    setFormData({
      id: priority.id,
      name: priority.name,
      color: priority.color,
      level: priority.level
    });
    setEditMode(true);
    setDialogOpen(true);
  };

  const handleAddNew = () => {
    setFormData({ id: null, name: '', color: '#3b82f6', level: 1 });
    setEditMode(false);
    setDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;
    try {
      await api.put(`/priorities/${itemToDelete.id}`, { is_active: false });
      toast.success('Priority deleted successfully!');
      setDeleteDialogOpen(false);
      setItemToDelete(null);
      fetchPriorities();
    } catch (error) {
      toast.error('Failed to delete priority');
    }
  };

  return (
    <Card className="border-slate-200 shadow-sm">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold">Priorities</h2>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={handleAddNew} className="bg-gradient-to-r from-indigo-600 to-indigo-500 hover:from-indigo-700 hover:to-indigo-600 shadow-lg shadow-indigo-500/30 text-white">
                <Plus className="mr-2 h-4 w-4" />
                Add Priority
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white">
              <DialogHeader>
                <DialogTitle>{editMode ? 'Edit Priority' : 'Create Priority'}</DialogTitle>
                <DialogDescription>{editMode ? 'Update priority details' : 'Add a new priority level'}</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit}>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="color">Color *</Label>
                    <div className="flex gap-2">
                      <Input
                        id="color"
                        type="color"
                        value={formData.color}
                        onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                        className="w-20 h-11"
                        required
                      />
                      <Input
                        type="text"
                        value={formData.color}
                        onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                        className="flex-1"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="level">Level (1-10) *</Label>
                    <Input
                      id="level"
                      type="number"
                      min="1"
                      max="10"
                      value={formData.level}
                      onChange={(e) => setFormData({ ...formData, level: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" disabled={submitting}>
                    {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                    {editMode ? 'Update' : 'Create'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent className="bg-white">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Priority</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete "{itemToDelete?.name}"? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {loading ? (
          <div className="text-center py-8">Loading...</div>
        ) : (
          <div className="space-y-3">
            {priorities.map((priority) => (
              <div key={priority.id} className="p-5 border border-slate-200 rounded-xl hover:border-indigo-200 hover:shadow-md transition-all bg-white flex items-center gap-3">
                <div
                  className="w-6 h-6 rounded-full border-2 border-slate-300"
                  style={{ backgroundColor: priority.color }}
                />
                <div className="flex-1">
                  <h3 className="font-semibold text-slate-900">{priority.name}</h3>
                  <p className="text-sm text-slate-600">Level: {priority.level}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEdit(priority)}
                    className="h-8 w-8 p-0 hover:bg-indigo-50"
                  >
                    <Edit2 className="h-4 w-4 text-indigo-600" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setItemToDelete(priority);
                      setDeleteDialogOpen(true);
                    }}
                    className="h-8 w-8 p-0 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4 text-red-600" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PrioritiesTab;